import {
  Add01Icon,
  AddMaleIcon,
  BankIcon,
  Button,
  Calendar03Icon,
  Location03Icon,
  Male02Icon,
  PencilEdit02Icon,
  SaleTag02Icon,
  SignatureIcon,
  StatusIcon,
  UserRoadsideIcon,
  ZapIcon,
} from "@porchpass/ui";
import * as React from "react";
import { useApplicationFormStore } from "../hooks/useApplicationFormStore";
import type { ApplicationStep } from "../types/applicationTypes";

// ============================================================================
// Constants
// ============================================================================

/** Steps shown on summary (excludes "summary" itself) */
const SUMMARY_STEPS: ApplicationStep[] = [
  "primary-borrower",
  "co-borrower",
  "property",
  "pricing",
  "lenders",
  "signature",
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const STEP_ICON_CONFIG: Record<string, { icon: React.FC<any>; color: string }> =
  {
    "primary-borrower": { icon: Male02Icon, color: "text-[#6f41e6]" },
    "co-borrower": { icon: AddMaleIcon, color: "text-[#6f41e6]" },
    property: { icon: Location03Icon, color: "text-[#e07a2f]" },
    pricing: { icon: SaleTag02Icon, color: "text-primary" },
    lenders: { icon: BankIcon, color: "text-[#687083]" },
    signature: { icon: SignatureIcon, color: "text-[#1453f5]" },
  };

// ============================================================================
// Component
// ============================================================================

interface SummarySectionProps {
  onNavigate: (step: ApplicationStep) => void;
  isLockedForSigning?: boolean;
}

export const SummarySection: React.FC<SummarySectionProps> = ({
  onNavigate,
  isLockedForSigning = false,
}) => {
  const store = useApplicationFormStore();
  const { data, getStepProgress, getOverallProgress, getStepConfigs } = store;
  const [filter, setFilter] = React.useState<"all" | "todo">("all");

  const overallProgress = getOverallProgress();
  const stepConfigs = getStepConfigs();

  // Filter to only the data steps (no summary)
  const dataSteps = stepConfigs.filter((s) => s.id !== "summary");

  // Co-borrower card visibility
  const hasCoBorrower = data.hasCoBorrower;

  // Filter logic
  const visibleSteps = React.useMemo(() => {
    let steps = dataSteps;
    if (filter === "todo") {
      steps = steps.filter((s) => {
        if (s.id === "co-borrower" && !hasCoBorrower) return false;
        return s.progress < 100;
      });
    }
    return steps;
  }, [dataSteps, filter, hasCoBorrower]);

  // Counts for filter tabs
  const allCount = hasCoBorrower
    ? dataSteps.length
    : dataSteps.filter((s) => s.id !== "co-borrower").length;
  const todoCount = dataSteps.filter((s) => {
    if (s.id === "co-borrower" && !hasCoBorrower) return false;
    return s.progress < 100;
  }).length;

  // Continue handler — go to first incomplete step
  const handleContinue = () => {
    if (isLockedForSigning) {
      onNavigate("signature");
      return;
    }
    const firstIncomplete = SUMMARY_STEPS.find((s) => {
      if (s === "co-borrower" && !hasCoBorrower) return false;
      return getStepProgress(s) < 100;
    });
    onNavigate(firstIncomplete ?? "primary-borrower");
  };

  // Handle card click — redirect to signature when locked
  const handleCardClick = (step: ApplicationStep) => {
    if (isLockedForSigning && step !== "signature") {
      onNavigate("signature");
      return;
    }
    onNavigate(step);
  };

  // Format date as MM-DD-YYYY
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "—";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${mm}-${dd}-${yyyy}`;
  };

  // Relative time
  const relativeTime = (dateStr?: string) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "—";
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins} mins ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? "s" : ""} ago`;
  };

  return (
    <div className="flex gap-10 w-full max-w-[860px] mx-auto">
      {/* Left column — Progress + Section cards */}
      <div className="flex-1 min-w-0 max-w-[500px]">
        {/* Progress bar + Filter tabs (same row) */}
        <div className="flex gap-3 items-center mb-6">
          <span className="text-sm font-medium whitespace-nowrap text-on-surface-muted">
            Progress
          </span>
          <div className="flex items-center gap-1 w-[99px]">
            <div
              className="h-1 bg-[#1453f5] rounded-full transition-all duration-300"
              style={{
                width: `${Math.max(0, Math.min(overallProgress, 100))}%`,
              }}
            />
            <div
              className="h-1 bg-[#e5ebfd] rounded-full transition-all duration-300"
              style={{
                width: `${Math.max(0, 100 - Math.min(overallProgress, 100))}%`,
              }}
            />
          </div>
          <span className="text-[10px] font-semibold text-on-surface-primary whitespace-nowrap">
            {overallProgress}%
          </span>

          {/* Filter tabs */}
          <div className="flex gap-2 ml-2 shrink-0">
            <button
              type="button"
              onClick={() => setFilter("all")}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                filter === "all"
                  ? "bg-[#e5ebfd] text-[#1453f5]"
                  : "text-[#4a5466] hover:bg-[#f0f1f4]"
              }`}
            >
              All
              <span
                className={`text-xs min-w-[18px] text-center px-1 py-0.5 rounded ${
                  filter === "all"
                    ? "bg-[#1453f5]/10 text-[#1453f5]"
                    : "bg-[#f0f1f4] text-[#4a5466]"
                }`}
              >
                {allCount}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setFilter("todo")}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                filter === "todo"
                  ? "bg-[#e5ebfd] text-[#1453f5]"
                  : "text-[#4a5466] hover:bg-[#f0f1f4]"
              }`}
            >
              To-do
              <span
                className={`text-xs min-w-[18px] text-center px-1 py-0.5 rounded ${
                  filter === "todo"
                    ? "bg-[#1453f5]/10 text-[#1453f5]"
                    : "bg-[#f4f1ee] text-[#8c6727]"
                }`}
              >
                {todoCount}
              </span>
            </button>
          </div>
        </div>

        {/* Section cards */}
        <div className="flex flex-col gap-2">
          {visibleSteps.map((step) => {
            // Co-borrower without data — show "Add" button
            if (step.id === "co-borrower" && !hasCoBorrower) {
              return (
                <button
                  key="add-co-borrower"
                  type="button"
                  onClick={() => handleCardClick("co-borrower")}
                  className="flex items-center justify-between px-[24px] py-[12px] border border-dashed border-[#d2d5db] rounded-[24px] bg-[#f9fafb] hover:bg-grey-50 transition-colors cursor-pointer"
                >
                  <span className="text-sm font-medium text-on-surface-muted">
                    Add a Co-Borrower
                  </span>
                  <div className="flex items-center justify-center size-[16px] rounded-full bg-[#687083] text-white">
                    <Add01Icon variant="stroke-rounded" size={8} />
                  </div>
                </button>
              );
            }

            const iconConfig = STEP_ICON_CONFIG[step.id];
            const Icon = iconConfig?.icon;
            const iconColor = iconConfig?.color ?? "text-on-surface-muted";
            const isComplete = step.progress === 100;

            return (
              <button
                key={step.id}
                type="button"
                onClick={() => handleCardClick(step.id)}
                className="flex items-center gap-4 px-5 py-4 rounded-xl bg-[#f0f1f4] hover:bg-[#e8e9ed] transition-colors cursor-pointer"
              >
                {/* Icon */}
                <div className="flex items-center justify-center size-[32px] rounded-full shrink-0 bg-white">
                  {Icon && (
                    <Icon
                      variant="bulk-rounded"
                      size={18}
                      className={iconColor}
                    />
                  )}
                </div>
                {/* Text */}
                <div className="flex flex-col flex-1 items-start min-w-0">
                  <span className="text-sm font-semibold text-on-surface-primary">
                    {step.label}
                  </span>
                  {step.description && (
                    <span className="text-xs text-on-surface-muted">
                      {step.description}
                    </span>
                  )}
                </div>
                {/* Completion indicator */}
                <div className="shrink-0">
                  {isComplete ? (
                    <div className="flex items-center justify-center size-4 rounded-full bg-[#017C3F]">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 14 14"
                        fill="none"
                      >
                        <path
                          d="M3.5 7.5L5.75 9.75L10.5 4.25"
                          stroke="white"
                          strokeWidth="1.75"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  ) : (
                    <div className="relative size-4">
                      <svg className="size-4" viewBox="0 0 16 16">
                        <circle
                          cx="8"
                          cy="8"
                          r="6"
                          fill="none"
                          stroke="#e4e7ec"
                          strokeWidth="2"
                        />
                        <circle
                          cx="8"
                          cy="8"
                          r="6"
                          fill="none"
                          stroke="#1453f5"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeDasharray={`${(step.progress / 100) * 37.7} 37.7`}
                          transform="rotate(-90 8 8)"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Continue button */}
        <div className="mt-6">
          <Button size="l" className="w-full h-[34px]" onClick={handleContinue}>
            Continue
          </Button>
        </div>
      </div>

      {/* Right column — Application info panel */}
      <div className="w-[302px] shrink-0">
        <div className="rounded-[14px] bg-[#f3f5f8] border border-[#e4e7ec] p-6 relative">
          {/* Borrower avatar + name (left) */}
          <div className="flex flex-col gap-[16px] items-start w-[117px] mb-[24px]">
            <div className="flex items-center justify-center size-[56px] rounded-full bg-white text-primary text-xl font-semibold uppercase">
              {data.primaryBorrower.lastName
                ? data.primaryBorrower.lastName.charAt(0).toUpperCase()
                : "?"}
            </div>
            <span className="text-lg font-semibold text-on-surface-primary">
              {data.primaryBorrower.firstName} {data.primaryBorrower.lastName}
            </span>
          </div>
          {/* App ID pill - positioned to align with avatar only */}
          <div className="absolute top-[41px] right-[23px]">
            <div className="flex items-center justify-center h-[20px] px-3 rounded-[40px] bg-white">
              <span className="text-xs text-on-surface-muted">
                {data.applicationId}
              </span>
            </div>
          </div>

          {/* Info rows */}
          <div className="divide-y divide-[#e4e7ec]">
            {/* Status */}
            <div className="h-[40px] flex items-center justify-between">
              <div className="flex gap-2 items-center text-on-surface-muted">
                <StatusIcon variant="stroke-rounded" size={16} />
                <span className="text-sm">Status</span>
              </div>
              <div className="flex items-center gap-1 px-2 py-1 bg-[#EDEDF0] rounded-full">
                <PencilEdit02Icon
                  variant="stroke-rounded"
                  size={9}
                  className="text-[#111827]"
                />
                <span className="text-xs font-medium text-[#111827] capitalize">
                  {data.status}
                </span>
              </div>
            </div>

            {/* Sales rep */}
            {data.salesRepName && (
              <div className="h-[40px] flex items-center justify-between">
                <div className="flex gap-2 items-center text-on-surface-muted">
                  <UserRoadsideIcon variant="stroke-rounded" size={16} />
                  <span className="text-sm">Sales rep</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-semibold text-on-surface-primary">
                    {data.salesRepName}
                  </span>
                </div>
              </div>
            )}

            {/* Created on */}
            {data.createdAt && (
              <div className="h-[40px] flex items-center justify-between">
                <div className="flex gap-2 items-center text-on-surface-muted">
                  <Calendar03Icon variant="stroke-rounded" size={16} />
                  <span className="text-sm">Created on</span>
                </div>
                <span className="text-sm font-semibold text-on-surface-primary">
                  {formatDate(data.createdAt)}
                </span>
              </div>
            )}

            {/* Last updated */}
            {data.updatedAt && (
              <div className="h-[40px] flex items-center justify-between">
                <div className="flex gap-2 items-center text-on-surface-muted">
                  <ZapIcon variant="stroke-rounded" size={16} />
                  <span className="text-sm">Last updated</span>
                </div>
                <span className="text-sm font-semibold text-on-surface-primary">
                  {relativeTime(data.updatedAt)}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
